import React from 'react';
import {
    FileText,
    Hammer,
    Building2,
    DollarSign,
    RefreshCw,
    CheckCircle,
    AlertTriangle,
    Info,
} from 'lucide-react';
import { formatDate } from '../../utils/formatters';
import type { UnitConfidence } from '../../types/calculations';
import { SectionTitle, GoldBadge } from '../Luxury';

// ========== DATA SOURCE CARD SUB-COMPONENT ==========

type ConfidenceLevel = 'high' | 'medium' | 'low' | 'unknown';

interface DataSourceCardProps {
    icon: React.ReactNode;
    label: string;
    value: string;
    confidence: ConfidenceLevel;
    detail?: string;
}

const confidenceColors: Record<ConfidenceLevel, { bg: string; border: string; text: string; icon: string }> = {
    high: { bg: 'bg-emerald/10', border: 'border-emerald/30', text: 'text-emerald', icon: 'text-emerald' },
    medium: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', icon: 'text-amber-400' },
    low: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', icon: 'text-red-400' },
    unknown: { bg: 'bg-slate-dark', border: 'border-slate-dark', text: 'text-stone-700', icon: 'text-stone-700' },
};

const confidenceIcons: Record<ConfidenceLevel, React.ReactNode> = {
    high: <CheckCircle className="w-3 h-3" />,
    medium: <Info className="w-3 h-3" />,
    low: <AlertTriangle className="w-3 h-3" />,
    unknown: <Info className="w-3 h-3" />,
};

const DataSourceCard: React.FC<DataSourceCardProps> = ({
    icon,
    label,
    value,
    confidence,
    detail,
}) => {
    const colors = confidenceColors[confidence];

    return (
        <div
            className={`p-4 rounded-lg border ${colors.bg} ${colors.border} transition-all hover:border-gold/50 hover:-translate-y-0.5`}
        >
            <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                    <div className="text-gold">{icon}</div>
                    <span className="text-sm font-semibold uppercase tracking-[0.12em] text-stone-700">
                        {label}
                    </span>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-sm font-semibold uppercase tracking-wider rounded ${colors.bg} ${colors.text}`}>
                    {confidenceIcons[confidence]}
                    {confidence.toUpperCase()}
                </span>
            </div>
            <p className="text-lg font-bold font-mono text-ivory">{value}</p>
            {detail && (
                <p className="mt-1 text-sm text-stone-700 truncate" title={detail}>
                    {detail}
                </p>
            )}
        </div>
    );
};

// ========== PROPERTY INTELLIGENCE PANEL ==========

interface PropertyIntelligencePanelProps {
    propertyId: string;
    // Data source status
    rentalLicense?: {
        status: string;
        unit_count?: number;
        expiration_date?: string;
        license_number?: string;
    };
    permitCount?: number;
    lastPermitDate?: string;
    // Confidence indicators
    unitConfidence?: UnitConfidence;
    unitSource?: string;
    rentConfidence?: string;
    rentMethodology?: string;
    // Data freshness
    dataFetchedAt?: string;
    // Refresh callback
    onRefresh?: () => void;
    isRefreshing?: boolean;
}

/**
 * Consolidated property intelligence panel showing all data sources
 * with confidence levels and data freshness indicators.
 * Luxury theme with charcoal background and gold accents.
 */
export const PropertyIntelligencePanel: React.FC<PropertyIntelligencePanelProps> = ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    propertyId: _propertyId,
    rentalLicense,
    permitCount = 0,
    lastPermitDate,
    unitConfidence,
    unitSource,
    rentConfidence,
    rentMethodology,
    dataFetchedAt,
    onRefresh,
    isRefreshing = false,
}) => {
    // Determine rental license confidence
    const getLicenseConfidence = (): ConfidenceLevel => {
        if (!rentalLicense?.status) return 'unknown';
        const status = rentalLicense.status.toUpperCase();
        if (status === 'ACTIVE' || status === 'FOUND') return 'high';
        if (status === 'EXPIRED') return 'medium';
        if (status === 'NOT_FOUND' || status === 'API_ERROR' || status === 'NOT_LOADED') return 'low';
        return 'unknown';
    };

    // Determine permit activity confidence based on count
    const getPermitConfidence = (): ConfidenceLevel => {
        if (permitCount >= 5) return 'high';
        if (permitCount >= 2) return 'medium';
        if (permitCount >= 1) return 'low';
        return 'unknown';
    };

    // Map UnitConfidence to local confidence level
    const mapUnitConfidence = (uc?: UnitConfidence): ConfidenceLevel => {
        if (!uc) return 'unknown';
        switch (uc) {
            case 'HIGH':
                return 'high';
            case 'MEDIUM':
                return 'medium';
            case 'LOW':
            case 'FALLBACK':
                return 'low';
            default:
                return 'unknown';
        }
    };

    // Map rent confidence string to local confidence level
    const mapRentConfidence = (rc?: string): ConfidenceLevel => {
        if (!rc) return 'unknown';
        const normalized = rc.toLowerCase();
        if (normalized === 'very_high' || normalized === 'high') return 'high';
        if (normalized === 'medium') return 'medium';
        if (normalized === 'low' || normalized === 'estimate') return 'low';
        return 'unknown';
    };

    // Format license status for display
    const getLicenseDisplay = (): string => {
        if (!rentalLicense?.status) return 'Unknown';
        const status = rentalLicense.status.toUpperCase();
        if (status === 'ACTIVE' || status === 'FOUND') return 'Active';
        if (status === 'EXPIRED') return 'Expired';
        if (status === 'NOT_FOUND') return 'Not Found';
        if (status === 'NOT_LOADED') return 'Loading...';
        if (status === 'API_ERROR') return 'API Error';
        return rentalLicense.status;
    };

    // Format permit activity for display
    const getPermitDisplay = (): string => {
        if (permitCount === 0) return 'None Found';
        return `${permitCount} permit${permitCount === 1 ? '' : 's'}`;
    };

    // Format unit source for display
    const formatUnitSource = (source?: string): string => {
        if (!source) return '';
        return source.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    // Format rent methodology for display
    const formatRentMethodology = (method?: string): string => {
        if (!method) return '';
        switch (method) {
            case 'rentometer_percentiles':
                return 'Rentometer 25th/75th percentile';
            case 'independent_comps':
                return 'Independent comp analysis';
            case 'percentile_split':
                return 'Percentile split method';
            case 'zandi_egi':
                return 'Zandi EGI benchmark';
            case 'psf_fallback':
                return 'PSF formula (fallback)';
            default:
                return method.replace(/_/g, ' ');
        }
    };

    return (
        <div className="bg-charcoal p-6 rounded-lg border border-slate-dark">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <SectionTitle>Property Intelligence</SectionTitle>
                    <GoldBadge variant="off-market">Due Diligence</GoldBadge>
                </div>
                {onRefresh && (
                    <button
                        onClick={onRefresh}
                        disabled={isRefreshing}
                        className="p-2 text-stone-700 hover:text-gold hover:bg-gold/10 rounded-lg transition-colors disabled:opacity-50"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </button>
                )}
            </div>

            {/* Data Sources Grid - 4 cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* 1. Rental License */}
                <DataSourceCard
                    icon={<FileText className="w-5 h-5" />}
                    label="Rental License"
                    value={getLicenseDisplay()}
                    confidence={getLicenseConfidence()}
                    detail={
                        // Show license number + units + expiration in detail line
                        [
                            rentalLicense?.license_number ? `#${rentalLicense.license_number}` : null,
                            rentalLicense?.unit_count ? `${rentalLicense.unit_count} units` : null,
                            rentalLicense?.expiration_date ? `Exp: ${formatDate(rentalLicense.expiration_date)}` : null,
                        ].filter(Boolean).join(' • ') || undefined
                    }
                />

                {/* 2. Permit Activity */}
                <DataSourceCard
                    icon={<Hammer className="w-5 h-5" />}
                    label="Permit Activity"
                    value={getPermitDisplay()}
                    confidence={getPermitConfidence()}
                    detail={
                        lastPermitDate
                            ? `Last: ${formatDate(lastPermitDate)}`
                            : permitCount > 0
                            ? 'Recent activity'
                            : 'No recent permits'
                    }
                />

                {/* 3. Unit Detection */}
                <DataSourceCard
                    icon={<Building2 className="w-5 h-5" />}
                    label="Unit Detection"
                    value={unitConfidence || 'Unknown'}
                    confidence={mapUnitConfidence(unitConfidence)}
                    detail={unitSource ? `Source: ${formatUnitSource(unitSource)}` : undefined}
                />

                {/* 4. Rent Validation */}
                <DataSourceCard
                    icon={<DollarSign className="w-5 h-5" />}
                    label="Rent Validation"
                    value={rentConfidence || 'Unknown'}
                    confidence={mapRentConfidence(rentConfidence)}
                    detail={rentMethodology ? formatRentMethodology(rentMethodology) : undefined}
                />
            </div>

            {/* Footer - Data Freshness */}
            {dataFetchedAt && (
                <div className="mt-4 pt-3 border-t border-slate-dark flex items-center justify-between text-sm text-stone-700">
                    <span>
                        Data as of {formatDate(dataFetchedAt)}
                    </span>
                    {onRefresh && (
                        <button
                            onClick={onRefresh}
                            disabled={isRefreshing}
                            className="text-gold hover:text-gold/80 hover:underline disabled:opacity-50"
                        >
                            {isRefreshing ? 'Refreshing...' : 'Refresh data'}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default PropertyIntelligencePanel;
